/**
 * Viking Dental Store — Custom JS Foundation
 * assets/viking-custom.js
 * Version: 2.1.1 | Design Upgrade + Animated Placeholder + Smart Search V2 (repaired)
 * Theme: streamline-theme-3-0-3 new version [UNPUBLISHED]
 *
 * 2026-06-17 FIX: the previously appended "VIKING SMART SEARCH V2" block had
 * raw Markdown artifacts ( **bold** and ``` code fences ) baked into the JS,
 * which made the ENTIRE file a SyntaxError so none of it executed (counters,
 * scroll fade-in reveal, wishlist, search all dead → blank sections).
 * The block is rebuilt as valid JS and the whole file is guarded.
 */

(function () {
  'use strict';

  /* Readiness flag: proves this file parsed & executed. critical-css.liquid
     keeps a dormant CSS fail-safe gated on html:not(.viking-js-ok); if this
     file ever fails to parse again, the class is never added and the fail-safe
     reveals every .viking-fade-in section so the page can't go blank. */
  if (document.documentElement) {
    document.documentElement.classList.add('viking-js-ok');
  }

  /* =====================================================
     NAMESPACE
     ===================================================== */
  window.Viking = window.Viking || {};

  /* =====================================================
     DOM READY
     ===================================================== */
  Viking.ready = function (fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  };

  /* =====================================================
     SAFE LOCALSTORAGE HELPERS
     ===================================================== */
  Viking.storage = {
    get: function (key) {
      try {
        var raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    },
    set: function (key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        return false;
      }
    },
    remove: function (key) {
      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },
    isAvailable: function () {
      try {
        var test = '__viking_test__';
        localStorage.setItem(test, '1');
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    }
  };

  /* =====================================================
     SAFE EVENT DELEGATION
     ===================================================== */
  Viking.on = function (parent, eventType, selector, handler) {
    if (!parent || !parent.addEventListener) return;
    parent.addEventListener(eventType, function (e) {
      var target = e.target ? e.target.closest(selector) : null;
      if (target && parent.contains(target)) {
        handler.call(target, e, target);
      }
    });
  };

  /* =====================================================
     INTERSECTION OBSERVER HELPER
     ===================================================== */
  Viking.onVisible = function (elements, callback, options) {
    var els = elements;
    if (!els) return;
    if (els.nodeType) els = [els];
    else if (els.length !== undefined) els = Array.prototype.slice.call(els);
    else els = [els];

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { callback(el, true); });
      return null;
    }

    var opts = Object.assign({
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    }, options || {});

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          callback(entry.target, true);
        }
      });
    }, opts);

    els.forEach(function (el) { observer.observe(el); });
    return observer;
  };

  /* =====================================================
     ONCE OBSERVER — unobserves after first trigger
     ===================================================== */
  Viking.onVisibleOnce = function (elements, callback, options) {
    var els = elements;
    if (!els) return;
    if (els.nodeType) els = [els];
    else if (els.length !== undefined) els = Array.prototype.slice.call(els);
    else els = [els];

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { callback(el, true); });
      return null;
    }

    var opts = Object.assign({
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    }, options || {});

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          callback(entry.target, true);
          obs.unobserve(entry.target);
        }
      });
    }, opts);

    els.forEach(function (el) { observer.observe(el); });
    return observer;
  };

  /* =====================================================
     COUNTER ANIMATION
     ===================================================== */
  Viking.animateCounter = function (el, target, duration, suffix, prefix) {
    if (!el) return;
    var startTime = null;
    var dur = duration || 2000;
    var sfx = suffix !== undefined ? suffix : '';
    var pfx = prefix !== undefined ? prefix : '';
    var numTarget = parseInt(target, 10) || 0;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / dur, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.floor(eased * numTarget);
      el.textContent = pfx + current.toLocaleString('en') + sfx;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = pfx + numTarget.toLocaleString('en') + sfx;
      }
    }

    if (window.requestAnimationFrame) {
      requestAnimationFrame(step);
    } else {
      el.textContent = pfx + numTarget.toLocaleString('en') + sfx;
    }
  };

  /* =====================================================
     FORMAT MONEY
     ===================================================== */
  Viking.formatMoney = function (cents, format) {
    var fmt = format
      || (window.theme && window.theme.settings && window.theme.settings.moneyFormat)
      || 'EGP {{amount}}';
    var value = (Math.round(cents) / 100).toFixed(2);
    return fmt
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.floor(Math.round(cents) / 100))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  };

  /* =====================================================
     DEBOUNCE
     ===================================================== */
  Viking.debounce = function (fn, wait) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait || 250);
    };
  };

  /* =====================================================
     THROTTLE
     ===================================================== */
  Viking.throttle = function (fn, limit) {
    var lastCall = 0;
    return function () {
      var now = Date.now();
      if (now - lastCall >= (limit || 200)) {
        lastCall = now;
        return fn.apply(this, arguments);
      }
    };
  };

  /* =====================================================
     SAFE FETCH WRAPPERS
     ===================================================== */
  Viking.fetch = function (url, options) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, options || {}))
      .then(function (res) {
        if (!res.ok) throw new Error('Viking: fetch error ' + res.status + ' for ' + url);
        return res;
      });
  };

  Viking.fetchJSON = function (url, options) {
    return Viking.fetch(url, options).then(function (res) { return res.json(); });
  };

  Viking.fetchText = function (url, options) {
    return Viking.fetch(url, options).then(function (res) { return res.text(); });
  };

  /* =====================================================
     TRAP FOCUS HELPER
     ===================================================== */
  Viking.trapFocus = function (container) {
    if (!container) return;
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  };

  /* =====================================================
     SCROLL-TRIGGERED FADE-IN
     Respects prefers-reduced-motion: makes all
     elements immediately visible if motion is reduced.
     ===================================================== */
  Viking.ready(function () {
    var prefersReducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
    var fadeEls = document.querySelectorAll('.viking-fade-in');
    if (!fadeEls.length) return;
    if (prefersReducedMotion) {
      Array.prototype.forEach.call(fadeEls, function (el) {
        el.classList.add('is-visible');
      });
    } else {
      Viking.onVisibleOnce(fadeEls, function (el) {
        el.classList.add('is-visible');
      });
    }
  });

  /* =====================================================
     WISHLIST (Phase 2)
     localStorage-backed, zero external dependencies.
     Item shape: { id, title, url, img, price, addedAt }
     ===================================================== */
  Viking.Wishlist = {
    STORAGE_KEY: 'viking_wishlist',

    getAll: function () {
      return Viking.storage.get(Viking.Wishlist.STORAGE_KEY) || [];
    },

    has: function (productId) {
      var id = String(productId);
      return Viking.Wishlist.getAll().some(function (item) {
        return String(item.id) === id;
      });
    },

    add: function (product) {
      var id = String(product.id);
      var items = Viking.Wishlist.getAll();
      if (!items.some(function (i) { return String(i.id) === id; })) {
        items.push({
          id:      id,
          title:   product.title || '',
          url:     product.url   || '',
          img:     product.img   || '',
          price:   Number(product.price) || 0,
          addedAt: Date.now()
        });
        Viking.storage.set(Viking.Wishlist.STORAGE_KEY, items);
      }
    },

    remove: function (productId) {
      var id = String(productId);
      var filtered = Viking.Wishlist.getAll().filter(function (i) {
        return String(i.id) !== id;
      });
      Viking.storage.set(Viking.Wishlist.STORAGE_KEY, filtered);
    },

    toggle: function (product) {
      if (Viking.Wishlist.has(product.id)) {
        Viking.Wishlist.remove(product.id);
        return false;
      }
      Viking.Wishlist.add(product);
      return true;
    },

    count: function () {
      return Viking.Wishlist.getAll().length;
    },

    updateButton: function (btn) {
      var id     = btn.getAttribute('data-viking-wishlist-product-id');
      var active = Viking.Wishlist.has(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', String(active));
      var title = btn.getAttribute('data-viking-wishlist-title') || '';
      var action = active ? 'Remove from Wishlist' : 'Save to Wishlist';
      btn.setAttribute('aria-label', title ? title + ' — ' + action : action);
      var textEl = btn.querySelector('.viking-wishlist-btn__text');
      if (textEl) textEl.textContent = active ? 'Saved' : 'Save to Wishlist';
    },

    updateAllButtons: function () {
      var btns = document.querySelectorAll('[data-viking-wishlist-product-id]');
      Array.prototype.forEach.call(btns, Viking.Wishlist.updateButton);
    },

    updateHeaderCount: function () {
      var els   = document.querySelectorAll('[data-viking-wishlist-count]');
      var count = Viking.Wishlist.count();
      Array.prototype.forEach.call(els, function (el) {
        el.textContent   = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    init: function () {
      Viking.Wishlist.updateAllButtons();
      Viking.Wishlist.updateHeaderCount();

      Viking.on(document.body, 'click', '[data-viking-wishlist-product-id]', function (e, btn) {
        e.preventDefault();
        var product = {
          id:    btn.getAttribute('data-viking-wishlist-product-id'),
          title: btn.getAttribute('data-viking-wishlist-title') || '',
          url:   btn.getAttribute('data-viking-wishlist-url')   || '',
          img:   btn.getAttribute('data-viking-wishlist-img')   || '',
          price: parseInt(btn.getAttribute('data-viking-wishlist-price') || 0, 10)
        };
        Viking.Wishlist.toggle(product);
        Viking.Wishlist.updateButton(btn);
        Viking.Wishlist.updateHeaderCount();
        btn.classList.add('viking-wishlist-btn--pulse');
        setTimeout(function () {
          btn.classList.remove('viking-wishlist-btn--pulse');
        }, 400);
      });
    }
  };

  Viking.ready(Viking.Wishlist.init);

  /* =====================================================
     SEARCH (Phase 3)
     Native Shopify predictive search — zero external deps.
     ===================================================== */
  Viking.Search = {
    MIN_CHARS: 2,
    MAX_RESULTS: 6,
    ENDPOINT: '/search/suggest.json',

    init: function () {
      var wraps = document.querySelectorAll('[data-viking-search]');
      Array.prototype.forEach.call(wraps, function (wrap) {
        Viking.Search._initWrap(wrap);
      });
    },

    _initWrap: function (wrap) {
      var input    = wrap.querySelector('.viking-search-input');
      var dropdown = wrap.querySelector('.viking-search-results-dropdown');
      var inner    = wrap.querySelector('.viking-search-results__inner');
      var loading  = wrap.querySelector('.viking-search-results__loading');
      var empty    = wrap.querySelector('.viking-search-results__empty');
      var clearBtn = wrap.querySelector('.viking-search-clear');
      var fetchId  = 0;

      if (!input || !dropdown) return;

      function showDropdown() {
        dropdown.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      }
      function hideDropdown() {
        dropdown.classList.remove('is-open');
        input.setAttribute('aria-expanded', 'false');
        if (inner) inner.innerHTML = '';
        hideLoading();
        hideEmpty();
      }
      function showLoading() {
        if (loading) loading.classList.add('is-visible');
        if (inner) inner.innerHTML = '';
        hideEmpty();
        showDropdown();
      }
      function hideLoading() {
        if (loading) loading.classList.remove('is-visible');
      }
      function showEmpty() {
        if (empty) empty.classList.add('is-visible');
        showDropdown();
      }
      function hideEmpty() {
        if (empty) empty.classList.remove('is-visible');
      }
      function showClear() {
        if (clearBtn) clearBtn.classList.add('is-visible');
      }
      function hideClear() {
        if (clearBtn) clearBtn.classList.remove('is-visible');
      }

      function renderResults(data, q) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        inner.innerHTML = '';
        if (!products.length) { showEmpty(); return; }
        hideEmpty();
        var fmt = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || 'EGP {{amount}}';
        var html = '';
        products.forEach(function (p) {
          var imgUrl = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
          var price = p.price
            ? fmt
                .replace('{{amount}}', (parseInt(p.price, 10) / 100).toFixed(2))
                .replace('{{amount_no_decimals}}', Math.floor(parseInt(p.price, 10) / 100))
            : '';
          var imgHtml = imgUrl
            ? '<img src="' + imgUrl + '" alt="" class="viking-search-result__img" width="56" height="56" loading="lazy">'
            : '<span class="viking-search-result__img-ph"></span>';
          html +=
            '<div class="viking-search-result-item" role="option">' +
              '<a href="' + p.url + '" class="viking-search-result__link" tabindex="0">' +
                '<span class="viking-search-result__thumb">' + imgHtml + '</span>' +
                '<span class="viking-search-result__meta">' +
                  (p.vendor ? '<span class="viking-search-result__vendor">' + p.vendor + '</span>' : '') +
                  '<span class="viking-search-result__title">' + p.title + '</span>' +
                  (price ? '<span class="viking-search-result__price">' + price + '</span>' : '') +
                '</span>' +
              '</a>' +
            '</div>';
        });
        html +=
          '<div class="viking-search-results__footer">' +
            '<a href="/search?q=' + encodeURIComponent(q) + '&type=product" class="viking-search-results__view-all">' +
              'View all results for "' + q + '" →' +
            '</a>' +
          '</div>';
        inner.innerHTML = html;
        showDropdown();
      }

      var doFetch = Viking.debounce(function (q) {
        var thisId = ++fetchId;
        showLoading();
        var url = Viking.Search.ENDPOINT +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product' +
          '&resources[limit]=' + Viking.Search.MAX_RESULTS +
          '&resources[options][unavailable_products]=last' +
          '&resources[options][fields]=title,vendor,variants.price';
        Viking.fetchJSON(url).then(function (data) {
          if (thisId !== fetchId) return;
          hideLoading();
          renderResults(data, q);
        }).catch(function () {
          if (thisId !== fetchId) return;
          hideDropdown();
        });
      }, 280);

      input.addEventListener('input', function () {
        var q = this.value.trim();
        q.length > 0 ? showClear() : hideClear();
        if (q.length < Viking.Search.MIN_CHARS) { hideDropdown(); return; }
        doFetch(q);
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { hideDropdown(); input.blur(); return; }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          var first = dropdown.querySelector('.viking-search-result-item a');
          if (first) first.focus();
        }
      });

      dropdown.addEventListener('keydown', function (e) {
        var links = Array.prototype.slice.call(dropdown.querySelectorAll('.viking-search-result-item a'));
        var idx = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && idx < links.length - 1) {
          e.preventDefault();
          links[idx + 1].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          idx === 0 ? input.focus() : links[idx - 1].focus();
        } else if (e.key === 'Escape') {
          hideDropdown();
          input.focus();
        }
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          input.value = '';
          hideClear();
          hideDropdown();
          input.focus();
        });
      }

      document.addEventListener('click', function (e) {
        if (!wrap.contains(e.target)) hideDropdown();
      });
    }
  };

  Viking.ready(Viking.Search.init);

  /* =====================================================
     STATS COUNTER (Phase 5)
     Animates [data-viking-stat] counters on scroll.
     Skips animation if prefers-reduced-motion is set.
     ===================================================== */
  Viking.Stats = {
    init: function () {
      var items = document.querySelectorAll('[data-viking-stat]');
      if (!items.length) return;

      var prefersReducedMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

      if (prefersReducedMotion || !window.IntersectionObserver) {
        Array.prototype.forEach.call(items, Viking.Stats._setFinal);
        return;
      }

      var observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var numEl = entry.target.querySelector('[data-target]');
          if (numEl && !numEl.hasAttribute('data-animated')) {
            numEl.setAttribute('data-animated', '1');
            var target = parseInt(numEl.getAttribute('data-target'), 10) || 0;
            var suffix = numEl.getAttribute('data-suffix') || '';
            Viking.animateCounter(numEl, target, 2200, suffix, '');
          }
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.3, rootMargin: '0px 0px -40px 0px' });

      Array.prototype.forEach.call(items, function (item) {
        observer.observe(item);
      });
    },

    _setFinal: function (item) {
      var numEl = item.querySelector('[data-target]');
      if (!numEl) return;
      var target = parseInt(numEl.getAttribute('data-target'), 10) || 0;
      var suffix = numEl.getAttribute('data-suffix') || '';
      numEl.textContent = target.toLocaleString('en') + suffix;
    }
  };

  Viking.ready(Viking.Stats.init);

  /* =====================================================
     ANIMATED SEARCH PLACEHOLDER (Phase 4)
     Typewriter effect cycling through dental phrases.
     Stops on focus; resumes on blur if input is empty.
     Respects prefers-reduced-motion — no animation if set.
     ===================================================== */
  Viking.SearchPlaceholder = {
    PREFIX: 'Search for ',
    PHRASES: [
      'orthodontic supplies...', 'endodontic files...', 'rotary files...',
      'restorative materials...', 'bonding agents...', 'brackets...',
      'archwires...', 'dental instruments...', 'impression materials...',
      'pediatric crowns...', 'implant tools...'
    ],
    INTERVAL_TYPE: 60,
    INTERVAL_DELETE: 35,
    PAUSE_AFTER: 1800,
    PAUSE_BEFORE: 400,

    init: function () {
      var reducedMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

      if (reducedMotion) return;

      var inputs = document.querySelectorAll('[data-viking-search] .viking-search-input');
      Array.prototype.forEach.call(inputs, function (input) {
        Viking.SearchPlaceholder._attach(input);
      });
    },

    _attach: function (input) {
      var sp = Viking.SearchPlaceholder;
      var phraseIndex = 0;
      var charIndex = 0;
      var isDeleting = false;
      var timer = null;

      function tick() {
        if (document.activeElement === input || input.value.length > 0) return;

        var phrase = sp.PHRASES[phraseIndex];

        if (!isDeleting) {
          charIndex++;
          input.setAttribute('placeholder', sp.PREFIX + phrase.substring(0, charIndex));
          if (charIndex === phrase.length) {
            isDeleting = true;
            timer = setTimeout(tick, sp.PAUSE_AFTER);
            return;
          }
        } else {
          charIndex--;
          input.setAttribute('placeholder', sp.PREFIX + phrase.substring(0, charIndex));
          if (charIndex === 0) {
            isDeleting = false;
            phraseIndex = (phraseIndex + 1) % sp.PHRASES.length;
            timer = setTimeout(tick, sp.PAUSE_BEFORE);
            return;
          }
        }

        timer = setTimeout(tick, isDeleting ? sp.INTERVAL_DELETE : sp.INTERVAL_TYPE);
      }

      input.addEventListener('focus', function () {
        clearTimeout(timer);
        timer = null;
        input.setAttribute('placeholder', sp.PREFIX + 'dental products...');
      });

      input.addEventListener('blur', function () {
        if (input.value.length === 0) {
          timer = setTimeout(tick, 800);
        }
      });

      tick();
    }
  };

  Viking.ready(Viking.SearchPlaceholder.init);

})();

/* =====================================================
   VIKING SMART SEARCH V2  (repaired 2026-06-17)
   Fuzzy/typo-tolerant routing + query rewriting for the
   storefront search. Collection routing for dental-student
   intent; canonical rewrites for common dental terms, typos
   and slang. Unmatched/random searches are left UNCHANGED.
   English-only. No external libs, no API calls, no AI.
   Self-contained + guarded; capture-phase submit + click.
   ===================================================== */
(function () {
  'use strict';

  try {
    if (window.VIKING_SMART_SEARCH_V2) return;
    window.VIKING_SMART_SEARCH_V2 = true;

    function vkNorm(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[-_\/]+/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function vkLev(a, b) {
      a = a || '';
      b = b || '';

      var al = a.length;
      var bl = b.length;

      if (Math.abs(al - bl) > 2) return 3;

      var prev = [];
      var cur = [];
      var i;
      var j;

      for (j = 0; j <= bl; j++) prev[j] = j;

      for (i = 1; i <= al; i++) {
        cur[0] = i;

        for (j = 1; j <= bl; j++) {
          var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;

          cur[j] = Math.min(
            prev[j] + 1,
            cur[j - 1] + 1,
            prev[j - 1] + cost
          );
        }

        for (j = 0; j <= bl; j++) prev[j] = cur[j];
      }

      return prev[bl];
    }

    function vkClose(token, target) {
      if (!token || !target || target.length < 5) return false;

      var max = target.length >= 9 ? 2 : 1;

      if (Math.abs(token.length - target.length) > max) return false;

      return vkLev(token, target) <= max;
    }

    function vkContainsPhrase(qTokens, phrase) {
      var p = String(phrase || '').split(' ');

      if (!p.length || p.length > qTokens.length) return false;

      for (var i = 0; i + p.length <= qTokens.length; i++) {
        var ok = true;

        for (var k = 0; k < p.length; k++) {
          if (qTokens[i + k] !== p[k]) {
            ok = false;
            break;
          }
        }

        if (ok) return true;
      }

      return false;
    }

    var VK_RULES = [
      {
        to: '/collections/dental-student',
        equals: ['student kit', 'dental student', 'dental kit', 'college kit', 'student supplies', 'dental student supplies', 'dental students', 'dental college kit'],
        phrases: ['dental student', 'student kit', 'dental kit', 'college kit', 'student supplies', 'dental student supplies', 'dental college kit'],
        fuzzy: []
      },
      {
        to: '/search?q=mini+screw',
        equals: ['mini screw', 'mini scrow', 'mini skrew', 'miniscrew', 'mini implant', 'tad', 'tads', 'orthodontic screw'],
        phrases: ['mini screw', 'mini implant', 'orthodontic screw', 'miniscrew', 'tad', 'tads'],
        fuzzy: ['miniscrew']
      },
      {
        to: '/search?q=o+ties',
        equals: ['o ties', 'otie', 'o tie', 'oties', 'o-ties', 'orthodontic ties', 'elastic ligatures'],
        phrases: ['o ties', 'o tie', 'otie', 'oties', 'orthodontic ties', 'elastic ligatures'],
        fuzzy: ['ligatures']
      },
      {
        to: '/search?q=power+chain',
        equals: ['power chain', 'pwer chain', 'powerchain', 'elastic chain', 'orthodontic chain'],
        phrases: ['power chain', 'pwer chain', 'powerchain', 'elastic chain', 'orthodontic chain'],
        fuzzy: ['powerchain']
      },
      {
        to: '/search?q=endo+files',
        equals: ['endo file', 'endo files', 'root canal file', 'root canal files', 'endodontic files', 'rotary file', 'rotary files'],
        phrases: ['endo file', 'endo files', 'root canal file', 'root canal files', 'endodontic file', 'endodontic files', 'rotary file', 'rotary files'],
        fuzzy: ['endodontic']
      },
      {
        to: '/search?q=alginate',
        equals: ['alginat', 'alginate', 'impression material'],
        phrases: ['impression material'],
        fuzzy: ['alginate']
      },
      {
        to: '/search?q=gutta+percha',
        equals: ['gutta', 'gutta percha', 'gp points'],
        phrases: ['gutta percha', 'gp points'],
        fuzzy: []
      },
      {
        to: '/search?q=bonding',
        equals: ['bond', 'bonding', 'adhesive'],
        phrases: ['bonding agent'],
        fuzzy: []
      },
      {
        to: '/search?q=etch',
        equals: ['etch', 'acid etch', 'etchant'],
        phrases: ['acid etch'],
        fuzzy: []
      },
      {
        to: '/search?q=glass+ionomer',
        equals: ['gic', 'glass ionomer'],
        phrases: ['glass ionomer'],
        fuzzy: []
      },
      {
        to: '/search?q=bracket',
        equals: ['bracket', 'brackets', 'braces'],
        phrases: [],
        fuzzy: []
      },
      {
        to: '/search?q=buccal+tube',
        equals: ['buccal tube', 'buccal tubes'],
        phrases: ['buccal tube', 'buccal tubes'],
        fuzzy: []
      }
    ];

    function vkRoute(raw) {
      var q = vkNorm(raw);

      if (!q) return null;

      var qt = q.split(' ');
      var i;
      var j;
      var k;

      for (i = 0; i < VK_RULES.length; i++) {
        if (VK_RULES[i].equals.indexOf(q) !== -1) {
          return VK_RULES[i].to;
        }
      }

      for (i = 0; i < VK_RULES.length; i++) {
        var ph = VK_RULES[i].phrases || [];

        for (j = 0; j < ph.length; j++) {
          if (vkContainsPhrase(qt, ph[j])) {
            return VK_RULES[i].to;
          }
        }
      }

      for (i = 0; i < VK_RULES.length; i++) {
        var fz = VK_RULES[i].fuzzy || [];

        for (j = 0; j < fz.length; j++) {
          for (k = 0; k < qt.length; k++) {
            if (vkClose(qt[k], fz[j])) {
              return VK_RULES[i].to;
            }

            if (k + 1 < qt.length && vkClose(qt[k] + qt[k + 1], fz[j])) {
              return VK_RULES[i].to;
            }
          }
        }
      }

      return null;
    }

    function vkInput(form) {
      if (!form || !form.querySelector) return null;

      return form.querySelector('input[name="q"]')
        || form.querySelector('.viking-search-input')
        || form.querySelector('input[type="search"]')
        || form.querySelector('input[type="text"]');
    }

    function vkIsSearchForm(form) {
      if (!form || !form.querySelector) return false;

      var action = String(form.getAttribute('action') || '');

      return action.indexOf('/search') !== -1
        || !!form.querySelector('input[name="q"]')
        || !!form.querySelector('input[type="search"]');
    }

    function vkGo(to, event) {
      if (!to || !event) return;

      event.preventDefault();
      event.stopPropagation();

      window.location.href = to;
    }

    document.addEventListener('submit', function (event) {
      var form = event.target;

      if (!form || form.tagName !== 'FORM') return;
      if (!vkIsSearchForm(form)) return;

      var input = vkInput(form);

      if (!input) return;

      var to = vkRoute(input.value);

      if (to) vkGo(to, event);
    }, true);

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('button[type="submit"], input[type="submit"], [data-viking-search-submit], .search__submit')
        : null;

      if (!button) return;

      var form = button.form || (button.closest ? button.closest('form') : null);

      if (!form) return;
      if (!vkIsSearchForm(form)) return;

      var input = vkInput(form);

      if (!input) return;

      var to = vkRoute(input.value);

      if (to) vkGo(to, event);
    }, true);
  } catch (e) {
    if (window.console && console.warn) {
      console.warn('Viking Smart Search V2 disabled:', e);
    }
  }
})();
